/** Dataobjects: attributes, relations, views, quick-add and Graphly mapping. */

export default {
	dataobjects: {
		empty: {
			title: "No dataobjects yet",
		},
		delete: "Delete dataobject",
		deleteConfirm: "Delete “{name}” from the information model? This cannot be undone.",
		loading: "Reading the information model…",
		undoShortcut: "Restore the last deleted attribute or relation",
		undoAlt: "Undo: press {key}",
		// The axiom editor — a dialog that works out one attribute from named premises.
		axiom: {
			title: "Axiom",
			titleNew: "New axiom",
			note: "Works out this attribute from the premises you name. Cases are checked top to bottom — the first one that matches sets the value.",
			noteReadOnly:
				"Works out this attribute from the premises below. Cases are checked top to bottom — the first one that matches sets the value.",
			// A project type is a closed set of values — the "option" kind of the type field.
			typeOption: "option · {name}",
			readOnly: "Read-only",
			noPermission: "Editing needs the “Axioms · Write” permission.",
			premises: {
				title: "Premises",
				hint: "What the cases can read",
				add: "Add premise",
				name: "Premise name",
				namePlaceholder: "name",
				nameRequired: "Give this premise a name",
				nameDuplicate: "Another premise already has this name",
				remove: "Remove premise {name}",
				removeUnnamed: "Remove unnamed premise",
				chooseHop: "choose attribute or relation",
				chooseFrom: "choose from {name}",
				changeHop: "Change {name}",
				usedBy: "Used by case {cases} | Used by cases {cases}",
				unused: "Not used by any case yet",
				emptyTitle: "No premises yet",
				emptyBody:
					"A premise gives a value a short name so the cases can test it — the order’s total, or its customer’s address.country.",
			},
			cases: {
				title: "Cases",
				hint: "checked top to bottom, first match wins",
				add: "Add case",
				colWhen: "When",
				colThen: "Then {name} is",
				always: "Always",
				kind: "Case kind",
				kindSet: "Set a value",
				kindCalc: "Calculate",
				duplicate: "Duplicate case {n}",
				remove: "Remove case {n}",
				collapse: "Collapse case {n}",
				expand: "Edit case {n}",
				move: "Move case {n}",
				moveHint: "Drag, or {key}+↑↓",
				when: "When",
				onlyWhen: "Only when",
				onlyWhenHint: "optional — leave empty to always apply",
				combine: "Combine conditions",
				and: "and",
				or: "or",
				removeCondition: "Remove condition",
				premise: "Premise",
				check: "Check",
				valueFor: "Value for {name}",
				noValue: "no value needed",
				setTo: "Then set {name} to",
				pickValue: "Pick a value",
				calcAs: "Calculate {name} as",
				insert: "Insert",
				exprHint: "A TypeScript expression; premises are available by name.",
				writeBack: "Write back",
				writeBackMeta: "optional · {set} of {total} set",
				writeBackBody:
					"When someone edits {name} directly, update each premise from it. Use {target} for the new value and {others} for the other premises. Leave a line empty to keep that premise as it is.",
				writeBackTo: "Write back to {name}",
				keep: "keep as it is",
				nothingMatched: "Nothing matched",
				unreachable: "Nothing matched — unreachable, case {n} always applies",
				staysEmpty: "{name} stays empty",
				emptyTitle: "Each case is one line of the rule",
				emptyBody:
					"A case either sets {name} to a fixed value when its conditions hold, or calculates it from the premises.",
				problems: {
					needsValue: "Pick a value",
					needsExpression: "Write an expression",
					conditionIncomplete: "Finish its conditions",
				},
			},
			// Which item of a to-many hop a premise reads.
			select: {
				which: "Which {name}",
				first: "first",
				second: "second",
				third: "third",
				nth: "item {n}",
				find: "where…",
				findLabel: "the one where…",
			},
			// The popover that adds one condition: a premise, then a check that fits it.
			addCondition: {
				title: "Add condition",
				premise: "Premise",
				check: "Check",
				hint: "Only the checks that fit {kind} are offered.",
				add: "Add",
				noPremises: "Name a premise first",
			},
			// Editing which item of a to-many hop a path reads.
			selectEditor: {
				title: "Which {name}",
				mode: "Pick by",
				byPosition: "Position",
				byCondition: "Condition",
				where: "Where",
				noAttributes: "{name} has no attributes to test.",
			},
			remove: "Remove axiom",
			removeConfirm: "Remove the axiom on “{name}”? This cannot be undone.",
			save: "Save axiom",
			create: "Create axiom",
			block: {
				noPremises: "Add a premise to save",
			},
			// The footer's list of what stands between the draft and Save, each a jump link.
			fix: {
				count: "{n} to fix:",
				premiseName: "premise {n} needs a name",
				premiseDuplicate: "premise {n} shares a name",
				premisePath: "premise {n} needs a complete path",
				premiseSelector: "premise {n} needs a finished “where”",
				caseValue: "case {n} needs a value",
				caseExpression: "case {n} needs an expression",
				caseCondition: "case {n} has an unfinished condition",
				unsaved: "Unsaved changes",
			},
		},
		attributes: {
			deleted: "Deleted attribute “{name}”",
			title: "Attributes",
			type: "Type",
			toggleOptional: "Optional attribute (click to make required)",
			toggleRequired: "Required attribute (click to make optional)",
			toggleArray: "List of values (click to make it a single value)",
			toggleScalar: "Single value (click to make it a list)",
			// Why `?` cannot be pressed where the type decides it (attributeModifiers.ts).
			optionalOnList:
				"A list of dataobjects is never optional: an empty list already means none.",
			optionalForCycle:
				"This type contains this dataobject again, so a single value must be optional — or make it a list.",
			// Beside such a type in the type picker.
			listOrOptional: "only [] or ?",
			// Over a type field whose type is a datatype.
			openTypeHint: "{key}-click to open in Datatypes",
			empty: "No attributes yet.",
			delete: "Delete attribute",
			openAxiom: "Configure axiom",
		},
		relations: {
			addTitle: "Add a relation",
			deleted: "Deleted relation “{name}”",
			title: "Relations",
			label: "Relation label",
			target: "Target",
			empty: "No relations yet.",
			dragHint: "To add a relation, {key}-drag this dataobject onto another on the canvas.",
			incoming: "Incoming relations",
			incomingJump: "Go to the dataobject that declares this relation",
			incomingHint:
				"Read-only. Edit an incoming relation on the dataobject that declares it.",
			delete: "Delete relation",
			deleteConfirm: "Delete relation “{name}”?",
			quickAdd: {
				placeholder: "name: Target",
				parseError: "Can't read that — expected name: Target",
				unknownTarget: "Unknown dataobject “{target}”",
				pickTarget: "pick a target from the menu, or append “: Dataobject”",
				groupFromTarget: "Create from target",
			},
		},
		centerGraph: "Center graph",
		permissions: {
			readOnly: "Read-only",
			readOnlyTooltip: "You don't have permission to edit dataobjects or this view.",
			dataobjectsReadOnly: "Dataobjects read-only",
			dataobjectsReadOnlyTooltip: "You can rearrange this view, but not edit dataobjects.",
			viewLocked: "View locked",
			viewLockedTooltip: "You can edit dataobjects, but not modify this view.",
			sidebarHint: "You don't have permission to edit dataobjects.",
		},
		sidebar: {
			idleHint: "Drag a dataobject onto the canvas, or double-click the canvas to add one.",
			idleHintReadOnly:
				"All dataobjects of this project — you don't have permission to change this view.",
		},
		changeColor: "Change colour",
		create: {
			title: "Add a dataobject",
			named: "Create “{name}”",
			notAllowed: "Creating dataobjects is not allowed",
			notAllowedTooltip: "You don't have permission to create dataobjects in this project.",
			searchOrCreateLabel: "Search, or type a label to create…",
			noResults: "No results",
		},
		quickAdd: {
			placeholder: "name: type[]?",
			parseError: "Can't read that — expected name: type[]?",
			unknownType: "Unknown type “{type}”",
			createHint: "Create {name}",
			pickType: 'pick a type from the menu, or append ": TypeName"',
			existsHint: "already exists — Enter jumps to it",
			groupExists: "Already exists",
			groupFromType: "Create from type",
			keys: {
				accept: "accept",
				create: "create",
				choose: "choose",
				close: "close",
			},
		},
		tabs: {
			label: "Dataobject properties",
			graphly: "Graphly",
		},
		graphly: {
			templateUrl: "Template URL",
			templatePlaceholder: "https://cdn.graphly.dev/...",
			templateHostNotAllowed:
				"Host not allowed — use the template's CDN URL (https://cdn.graphly.dev/…).",
			visitTemplate: "View template",
			browseTemplates: "Browse templates",
			fieldMapping: "Field mapping",
			mapAttribute: "Map to attribute…",
			defaultValue: "Default value",
			/* Under a mapped field's default: when the shape shows it instead of the attribute. */
			defaultWhileUnset: "Shown while the attribute is not set or not valid",
			boolTrue: "True",
			boolFalse: "False",
			/* An enum value that is the empty string, as the template allows it. */
			enumEmpty: "(empty)",
			noFields: "This template exposes no payload fields.",
		},
	},
	fields: {
		tags: "Tags",
		tagsPlaceholder: "Add tags…",
		tagsCreate: "Add “{tag}”",
		tagsEmpty: "No matching tags",
	},
	views: {
		create: "New view",
		edit: "Edit view",
		name: "View name",
		search: "Search views…",
		noResults: "No views match your search.",
		empty: {
			title: "No views yet",
			hint: "Create a view to visualize your information model.",
		},
		deleteConfirm: "Delete view “{name}”?",
		removeDataobject: "Remove from view",
		removeDataobjectConfirm:
			"Remove “{name}” from this view? It stays in the information model.",
		picker: {
			search: "Search",
			available: "Available",
			alreadyIn: "Already in view",
			noMatches: "No matches",
		},
	},
};
